export const CAPTCHA_BOT_STATUS = "CAPTCHA";

/**
 * Statuses a task can still be in while logging in. The live-view session is
 * opened at the start of the login and closed the moment it lands, so these are
 * the only statuses during which one can exist.
 */
const LOGIN_PHASE_STATUSES = [
  "PENDING",
  "QUEUED",
  "NOT_LOGGED_IN",
  "ECS_CONTAINER_STARTED",
  "TWOFA",
  "2FA",
  CAPTCHA_BOT_STATUS,
];

/**
 * The operator can open this bot's browser.
 *
 * The bot status is checked as well as the session status: the row is closed
 * when the login lands, so a row still claiming READY past that is a stale read
 * rather than an open window, and offering the button on it hands out a dead
 * address.
 */
export function isLiveViewReady(
  liveViewStatus?: string | null,
  botWorkStatus?: string | null,
): boolean {
  if ((liveViewStatus || "").toUpperCase() !== "READY") return false;
  const bot = (botWorkStatus || "").toUpperCase();
  return !bot || LOGIN_PHASE_STATUSES.includes(bot);
}

/**
 * The window should take over the screen by itself: the bot is on a captcha and
 * only a human in its browser can move it on.
 *
 * Driven by the session's own phase rather than by a websocket event. The bot
 * publishes live_view.updated on every change it makes - leasing a slot, going
 * ready, changing phase, closing - so treating the event as "open the window"
 * put it up during the login and again once it was over.
 */
export function isLiveViewNeeded(
  liveViewStatus?: string | null,
  liveViewPhase?: string | null,
  botWorkStatus?: string | null,
): boolean {
  return (
    isLiveViewReady(liveViewStatus, botWorkStatus) &&
    (liveViewPhase || "").toUpperCase() === CAPTCHA_BOT_STATUS
  );
}

/**
 * The window may impose itself only on the operator who started the run.
 * Everyone else keeps the manual "Live browser" button; a row with no
 * initiator (a run from before the field existed, or an API run) opens for
 * nobody.
 */
export function shouldAutoOpenLiveView(
  liveViewStatus: string | null | undefined,
  liveViewPhase: string | null | undefined,
  botWorkStatus: string | null | undefined,
  startedBy: number | null | undefined,
  viewerId: number | null | undefined,
): boolean {
  return (
    startedBy != null &&
    viewerId != null &&
    startedBy === viewerId &&
    isLiveViewNeeded(liveViewStatus, liveViewPhase, botWorkStatus)
  );
}
